/*eslint max-lines-per-function: ["error", 360]*/
import React, { useCallback, useEffect } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { useNavigation } from '../../../common/navigation';
import { TabsHistory } from '../../../app/components/TabsHistory';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  JobTypeEnum,
  KSFStatusEnum,
  SalesInvoiceStatusEnum,
} from '../../../common/pokCore/validation/enums';
import { SalesInvoiceSearch } from '../../components/SalesInvoices/SalesInvoiceSearch';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { GetCompanyDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { JobConfigInfo } from '../../components/JobConfig/JobConfigInfo';

enum SalesInvoicesTabs {
  DRAFTS_INVOICES = 'draftsInvoices',
  GENERATED_INVOICES = 'generatedInvoices',
  WAITING_FOR_KSEF = 'waitingForKSeF',
  REJECTED_FROM_KSEF = 'rejectedFromKSeF',
  OFFLINE_KSEF = 'offlineKSeF',
  ALL_INVOICES = 'allInvoices',
}

export const SalesInvoiceView: React.FC = () => {
  const nav = useNavigation();
  const pok = usePokCore();
  const { t, tk } = useTranslation('fvsLng');
  const [company, setCompany] = React.useState<null | GetCompanyDto>(null);

  useEffect(() => {
    pok.companies
      .getById(pok.getCompanyId())
      .then(setCompany)
      .catch(() => setCompany(null));
  }, [pok.companies, pok]);

  const getDraftData = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.salesInvoices.getAllByPortion(
        t,
        tk,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Draft,
        undefined,
      ),
    [pok.salesInvoices, t, tk],
  ) as GridGetterFunction;

  const getGeneratedData = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.salesInvoices.getAllByPortion(
        t,
        tk,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Generated,
        undefined,
      ),
    [pok.salesInvoices, t, tk],
  ) as GridGetterFunction;

  const getWaitingForKsefData = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.salesInvoices.getAllByPortion(
        t,
        tk,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Sent,
        KSFStatusEnum.WaitingForKSeF,
      ),
    [pok.salesInvoices, t, tk],
  ) as GridGetterFunction;

  const getRejectedFromKsefData = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.salesInvoices.getAllByPortion(
        t,
        tk,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Sent,
        KSFStatusEnum.RejectedInKSeF,
      ),
    [pok.salesInvoices, t, tk],
  ) as GridGetterFunction;

  const getAllData = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.salesInvoices.getAllByPortion(
        t,
        tk,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
      ),
    [pok.salesInvoices, t, tk],
  ) as GridGetterFunction;

  const getDraftCount = useCallback(
    (filterText, filterColumns) =>
      pok.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Draft,
        undefined,
      ),
    [pok.salesInvoices],
  ) as GridCountFunction;

  const getGeneratedCount = useCallback(
    (filterText, filterColumns) =>
      pok.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Generated,
        undefined,
      ),
    [pok.salesInvoices],
  ) as GridCountFunction;

  const getWaitingForKsefCount = useCallback(
    (filterText, filterColumns) =>
      pok.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Sent,
        KSFStatusEnum.WaitingForKSeF,
      ),
    [pok.salesInvoices],
  ) as GridCountFunction;

  const getRejectedFromKsefCount = useCallback(
    (filterText, filterColumns) =>
      pok.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Sent,
        KSFStatusEnum.RejectedInKSeF,
      ),
    [pok.salesInvoices],
  ) as GridCountFunction;

  const getOfflineKsefData = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.salesInvoices.getAllByPortion(
        t,
        tk,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Sent,
        KSFStatusEnum.OFFLINE,
      ),
    [pok.salesInvoices, t, tk],
  ) as GridGetterFunction;

  const getOfflineKsefCount = useCallback(
    (filterText, filterColumns) =>
      pok.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        SalesInvoiceStatusEnum.Sent,
        KSFStatusEnum.OFFLINE,
      ),
    [pok.salesInvoices],
  ) as GridCountFunction;

  const getAllCount = useCallback(
    (filterText, filterColumns) =>
      pok.salesInvoices.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
      ),
    [pok.salesInvoices],
  ) as GridCountFunction;

  const onRowClick = (salesInvoiceId: string, newWindow?: boolean) => {
    nav.salesInvoiceEditor({ salesInvoiceId, newWindow });
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.finance.salesInvoices)}</Card.Title>
        <TabsHistory
          activeKey={SalesInvoicesTabs.DRAFTS_INVOICES}
          id="sales-invoices-tabs"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={SalesInvoicesTabs.DRAFTS_INVOICES}
            title={t(tk.finance.drafts)}
            className="m-3"
          >
            <SalesInvoiceSearch
              getCount={getDraftCount}
              getData={getDraftData}
              onRowClick={onRowClick}
              hideStatusColumn
              withFilterInColumns
              showNewWindowButton
            />
          </Tab>
          <Tab
            eventKey={SalesInvoicesTabs.GENERATED_INVOICES}
            title={t(tk.finance.approved)}
            className="m-3"
          >
            <SalesInvoiceSearch
              getCount={getGeneratedCount}
              getData={getGeneratedData}
              onRowClick={onRowClick}
              hideStatusColumn
              withFilterInColumns
              showNewWindowButton
            />
          </Tab>
          {company?.ksef && (
            <Tab
              eventKey={SalesInvoicesTabs.WAITING_FOR_KSEF}
              title={t(tk.finance.waitingForKSeF)}
              className="m-3"
            >
              <SalesInvoiceSearch
                getCount={getWaitingForKsefCount}
                getData={getWaitingForKsefData}
                onRowClick={onRowClick}
                hideStatusColumn
                withFilterInColumns
                showNewWindowButton
              />
              <JobConfigInfo
                items={[
                  {
                    jobType: JobTypeEnum.ImportKSeFFromSymphonyHandel,
                    nextStartLabel: t(tk.prLang.ksfNextStart),
                    frequencyLabel: t(tk.prLang.frequency),
                  },
                ]}
              />
            </Tab>
          )}
          {company?.ksef && (
            <Tab
              eventKey={SalesInvoicesTabs.REJECTED_FROM_KSEF}
              title={t(tk.finance.rejectedFromKSeF)}
              className="m-3"
            >
              <SalesInvoiceSearch
                getCount={getRejectedFromKsefCount}
                getData={getRejectedFromKsefData}
                onRowClick={onRowClick}
                hideStatusColumn
                withFilterInColumns
                showNewWindowButton
              />
            </Tab>
          )}
          {company?.ksef && (
            <Tab
              eventKey={SalesInvoicesTabs.OFFLINE_KSEF}
              title={t(tk.finance.offlineKSeF)}
              className="m-3"
            >
              <SalesInvoiceSearch
                getCount={getOfflineKsefCount}
                getData={getOfflineKsefData}
                onRowClick={onRowClick}
                hideStatusColumn
                withFilterInColumns
                showNewWindowButton
              />
            </Tab>
          )}
          <Tab
            eventKey={SalesInvoicesTabs.ALL_INVOICES}
            title={t(tk.finance.all)}
            className="m-3"
          >
            <SalesInvoiceSearch
              getCount={getAllCount}
              getData={getAllData}
              onRowClick={onRowClick}
              withFilterInColumns
              showNewWindowButton
              withPayment={
                process.env.REACT_APP_SALES_INVOICES_WITH_PAYMENTS?.toLocaleLowerCase() ===
                  'on' && !!company?.symphonyHandel
              }
            />
            {process.env.REACT_APP_SALES_INVOICES_WITH_PAYMENTS?.toLocaleLowerCase() ===
              'on' &&
              !!company?.symphonyHandel && (
                <JobConfigInfo
                  items={[
                    {
                      jobType: JobTypeEnum.RefreshSalesInvoicePayments,
                      nextStartLabel: t(tk.prLang.refreshFVSPaymentsNextStart),
                      frequencyLabel: t(tk.prLang.frequency),
                    },
                  ]}
                />
              )}
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
